import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Link, Text, useAppTheme } from '@workwhile/ui'
import { usePositionDetailContextValue } from '../../context/PositionDetailProvider'
import { ArrowRight } from 'lucide-react'
import { ActionsCell } from '../ActionsCell'
import { format } from 'date-fns'

export type Requirements = ReturnType<
  typeof usePositionDetailContextValue
>['state']['requirements']

const VerificationTypeColumn: ColumnDef<Requirements[number]> = {
  header: 'Verification Type',
  accessorKey: 'verificationType',
  minSize: 200,
  maxSize: 200,
  cell: ({ row }) => {
    const { verificationType } = row.original

    return <Text>{verificationType}</Text>
  },
}

const CategoryColumn: ColumnDef<Requirements[number]> = {
  header: 'Category',
  accessorKey: 'category',
  minSize: 200,
  cell: ({ row }) => {
    const { category } = row.original

    return <Text>{category}</Text>
  },
}

const RequirementIdColumn: ColumnDef<Requirements[number]> = {
  header: 'Requirement ID',
  accessorKey: 'id',
  minSize: 200,
  maxSize: 256,
  cell: ({ row }) => {
    const { id } = row.original

    return <Text>{id}</Text>
  },
}

const RequirementNameColumn: ColumnDef<Requirements[number]> = {
  header: 'Requirement Name',
  accessorKey: 'name',
  minSize: 200,
  cell: ({ row }) => {
    const { name } = row.original

    return <Text>{name}</Text>
  },
}

const VisibleToWorkerColumn: ColumnDef<Requirements[number]> = {
  header: 'Visible to Worker',
  accessorKey: 'isWorkerVisible',
  minSize: 200,
  maxSize: 256,
  cell: ({ row }) => {
    const { isWorkerVisible } = row.original

    return <Text>{isWorkerVisible ? 'Yes' : 'No'}</Text>
  },
}

const MustHaveColumn: ColumnDef<Requirements[number]> = {
  header: 'Must Have',
  accessorKey: 'mustHave',
  minSize: 200,
  maxSize: 200,
  cell: ({ row }) => {
    const { mustHave } = row.original

    return <Text>{mustHave ? 'Yes' : 'No'}</Text>
  },
}

const AddedAtColumn: ColumnDef<Requirements[number]> = {
  header: 'Added At',
  accessorKey: 'createdAt',
  minSize: 200,
  maxSize: 200,
  cell: ({ row }) => {
    const { addedAt } = row.original
    return <Text>{format(addedAt, 'MMM dd, yyyy')}</Text>
  },
}

export const selectableColumns = [
  {
    id: 'verificationType',
    label: 'Verification Type',
    columnDef: VerificationTypeColumn,
  },
  {
    id: 'category',
    label: 'Category',
    columnDef: CategoryColumn,
  },
  {
    id: 'requirementId',
    label: 'Requirement ID',
    columnDef: RequirementIdColumn,
  },
  {
    id: 'requirementName',
    label: 'Requirement Name',
    columnDef: RequirementNameColumn,
  },
  {
    id: 'visibleToWorker',
    label: 'Visible to Worker',
    columnDef: VisibleToWorkerColumn,
  },
  {
    id: 'mustHave',
    label: 'Must Have',
    columnDef: MustHaveColumn,
  },
  {
    id: 'addedAt',
    label: 'Added At',
    columnDef: AddedAtColumn,
  },
] as const

export const DetailColumn: ColumnDef<Requirements[number]> = {
  header: 'Detail',
  size: 85,
  cell: ({ row }) => {
    const { colors } = useAppTheme()
    return (
      <Link to={`/requirements/${row.original.id}`}>
        View <ArrowRight size={14} color={colors.primary} />
      </Link>
    )
  },
}

export const ActionsColumn: ColumnDef<Requirements[number]> = {
  header: 'Actions',
  size: 70,
  cell: ({ row }) => <ActionsCell requirement={row.original} />,
}
